import React, { useEffect, useMemo, useRef, useState } from 'react';
import tenantData from '@data';
import tenantUtils from '@utils';
import { Select } from 'antd';
import debounce from 'lodash/debounce';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import Algolia from '../../../../services/algolia';
import { SearchIcon } from './icons';
import { isIndividualSeller as checkIndividualSeller } from './utils';
import { FiltersRow, SearchBox, SearchInput } from './ad-license-list-filters.styled';

const SORT_OPTIONS = [
  { value: 'rega_ad_license_issue_date desc', labelKey: 'Newest first' },
  { value: 'rega_ad_license_end_date asc', labelKey: 'Expiring first' },
];

const AdLicenseListFilters = ({ query, onChange, isMobile = false }) => {
  const { t } = useTranslation();
  // Every filter control is the same width + height so the row reads uniform;
  // 190px fits the longest label ("All property types") without truncating.
  const controlStyle = isMobile ? { width: '100%' } : { width: 190 };
  const { user: loginUser } = useSelector((state) => state.app.loginUser);
  const userGroupList = useSelector((state) => state.app.userGroup?.list) || [];
  const isIndividual = checkIndividualSeller(loginUser);

  const [searchValue, setSearchValue] = useState(query?.['q[rega_ad_license_ad_license_number_text_cont]'] || '');
  const [districtOptions, setDistrictOptions] = useState([]);

  const debouncedSearch = useRef(
    debounce((value) => onChange({ 'q[rega_ad_license_ad_license_number_text_cont]': value || undefined }), 300),
  ).current;

  const propertyTypeOptions = useMemo(() => {
    const purposes = (tenantData.listingTypes && tenantData.listingTypes()) || [];
    const map = new Map();
    purposes.forEach((purpose) =>
      (purpose.sub_types || []).forEach((subType) => {
        if (subType?.id != null && !map.has(subType.id)) map.set(subType.id, subType);
      }),
    );
    return [...map.values()].map((subType) => ({
      value: subType.id,
      label: tenantUtils.getLocalisedString(subType, 'title'),
    }));
  }, []);

  const agentValue = query?.['q[user_id_null]']
    ? 'unassigned'
    : query?.['q[user_id_eq]'] != null
      ? query['q[user_id_eq]']
      : 'all';

  const agents = loginUser?.is_agency_admin ? userGroupList : loginUser ? [loginUser] : [];
  const agentOptions = [
    { value: 'all', label: t('All agents') },
    ...agents
      .filter((agent) => agent?.id && agent.id !== -1)
      .map((agent) => ({ value: agent.id, label: tenantUtils.getLocalisedString(agent, 'name') })),
    { value: 'unassigned', label: t('Unassigned', { context: 'adLicense' }) },
  ];

  const onAgentChange = (value) => {
    if (value === 'all') {
      onChange({ 'q[user_id_eq]': undefined, 'q[user_id_null]': undefined });
    } else if (value === 'unassigned') {
      onChange({ 'q[user_id_eq]': undefined, 'q[user_id_null]': true });
    } else {
      onChange({ 'q[user_id_eq]': value, 'q[user_id_null]': undefined });
    }
  };

  const fetchDistricts = async (text, onSuccess = () => {}, onError = () => {}) => {
    // Same Algolia call the City/Location filter makes — districts only (numericFilters
    // level > city == level > 2) — queried against whatever the user types, capped at 10.
    const queryObject = { ...Algolia.getAllLocationsByName(text || ''), hitsPerPage: 10 };
    try {
      const response = await Algolia.getLocationsIndex().search('', queryObject);
      setDistrictOptions(response?.hits ?? []);
      onSuccess(response);
      return response;
    } catch (error) {
      onError();
      return null;
    }
  };

  // Prefetch the first 10 districts so the dropdown isn't empty before the user types.
  useEffect(() => {
    fetchDistricts('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Debounced district search for the antd Select's onSearch (ref keeps it off a stale closure).
  const fetchDistrictsRef = useRef(fetchDistricts);
  fetchDistrictsRef.current = fetchDistricts;
  const debouncedDistrictSearch = useMemo(
    () => debounce((text) => fetchDistrictsRef.current(text), 300),
    [],
  );

  const districtSelectOptions = districtOptions.map((hit) => ({
    value: hit.location_id,
    label: tenantUtils.getLocalisedString(hit, 'title'),
  }));

  return (
    <FiltersRow $isMobile={isMobile}>
      <SearchBox $isMobile={isMobile}>
        <SearchIcon size={16} color="rgb(157,157,157)" />
        <SearchInput
          type="text"
          value={searchValue}
          onChange={(e) => {
            setSearchValue(e.target.value);
            debouncedSearch(e.target.value);
          }}
          placeholder={t('Search license number')}
        />
      </SearchBox>

      <Select
        showSearch
        optionFilterProp="label"
        style={controlStyle}
        value={query?.['q[rega_ad_license_property_type_id_eq]'] ?? 'all'}
        onChange={(value) => onChange({ 'q[rega_ad_license_property_type_id_eq]': value === 'all' ? undefined : value })}
        options={[{ value: 'all', label: t('All property types') }, ...propertyTypeOptions]}
        getPopupContainer={(trigger) => trigger.parentNode}
      />

      {!isIndividual && (
        <Select
          showSearch
          optionFilterProp="label"
          style={controlStyle}
          value={agentValue}
          onChange={onAgentChange}
          options={agentOptions}
          getPopupContainer={(trigger) => trigger.parentNode}
        />
      )}

      <Select
        showSearch
        filterOption={false}
        style={controlStyle}
        value={query?.['q[rega_ad_license_location_id_eq]'] || undefined}
        placeholder={t('All districts')}
        onSearch={debouncedDistrictSearch}
        onChange={(value) => onChange({ 'q[rega_ad_license_location_id_eq]': value || undefined })}
        options={districtSelectOptions}
        notFoundContent={null}
        allowClear
        getPopupContainer={(trigger) => trigger.parentNode}
      />

      <Select
        style={controlStyle}
        value={query?.['q[s]'] || 'rega_ad_license_issue_date desc'}
        onChange={(value) => onChange({ 'q[s]': value })}
        options={SORT_OPTIONS.map((option) => ({ value: option.value, label: t(option.labelKey) }))}
        getPopupContainer={(trigger) => trigger.parentNode}
      />
    </FiltersRow>
  );
};

export default AdLicenseListFilters;
